import { useCallback, useEffect, useRef } from 'react'
import { FormFieldProps } from '../services/api/types'

type FormTrackingEvent =
  | 'form_start'
  | 'form_progress'
  | 'form_error'
  | 'form_submit'
  | 'form_abandonment'

interface FormTrackingPayload {
  event: FormTrackingEvent
  formId?: string
  formName: string
  [key: string]: any
}

const PROGRESS_THRESHOLDS = [25, 50, 75]

const pushToDataLayer = (payload: FormTrackingPayload) => {
  if (typeof window === 'undefined') return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    timestamp: new Date().toISOString(),
    ...payload
  })
}

export function useFormTracking(formName: string, formId?: string) {
  const hasStarted = useRef(false)
  const hasFinished = useRef(false)
  const firedThresholds = useRef<Set<number>>(new Set())

  const trackStart = useCallback(() => {
    if (hasStarted.current) return
    hasStarted.current = true
    pushToDataLayer({ event: 'form_start', formName, formId })
  }, [formName, formId])

  const trackProgress = useCallback(
    (completedFields: number, totalFields: number) => {
      if (!totalFields) return
      const percentage = Math.round((completedFields / totalFields) * 100)
      PROGRESS_THRESHOLDS.forEach(threshold => {
        if (
          percentage >= threshold &&
          !firedThresholds.current.has(threshold)
        ) {
          firedThresholds.current.add(threshold)
          pushToDataLayer({
            event: 'form_progress',
            formName,
            formId,
            progress: threshold
          })
        }
      })
    },
    [formName, formId]
  )

  const trackError = useCallback(
    (errorFields?: string[]) => {
      pushToDataLayer({
        event: 'form_error',
        formName,
        formId,
        errorFields
      })
    },
    [formName, formId]
  )

  const trackSubmit = useCallback(() => {
    hasFinished.current = true
    pushToDataLayer({ event: 'form_submit', formName, formId })
  }, [formName, formId])

  useEffect(() => {
    const trackAbandonment = () => {
      if (hasStarted.current && !hasFinished.current) {
        hasFinished.current = true
        pushToDataLayer({ event: 'form_abandonment', formName, formId })
      }
    }

    window.addEventListener('beforeunload', trackAbandonment)
    return () => {
      trackAbandonment()
      window.removeEventListener('beforeunload', trackAbandonment)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { trackStart, trackProgress, trackError, trackSubmit }
}

/**
 * Wires a Formik instance into useFormTracking: form_start + form_progress on
 * value changes, form_error on failed submit. Returns trackSubmit / trackError
 * for the onSubmit handler.
 */
export function useFormikTracking(
  formName: string,
  formId: string | undefined,
  formik: {
    values: Record<string, any>
    initialValues: Record<string, any>
    errors: Record<string, any>
    isValid: boolean
    submitCount: number
  },
  fields?: Array<Pick<FormFieldProps, 'name'> & Partial<FormFieldProps>>
) {
  const tracking = useFormTracking(formName, formId)
  const trackableFieldNames =
    fields
      ?.filter(
        field => field.type !== 'hidden' && field.field_block !== 'recaptcha'
      )
      .map(field => field.name) || []

  useEffect(() => {
    const isFilled = (value: any) =>
      Array.isArray(value) ? value.length > 0 : Boolean(value)
    const filledFieldCount = trackableFieldNames.filter(name =>
      isFilled(formik.values[name])
    ).length
    const touchedByUser = trackableFieldNames.some(
      name =>
        isFilled(formik.values[name]) &&
        formik.values[name] !== formik.initialValues[name]
    )
    if (!touchedByUser) {
      return
    }
    tracking.trackStart()
    tracking.trackProgress(filledFieldCount, trackableFieldNames.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values])

  useEffect(() => {
    if (formik.submitCount > 0 && !formik.isValid) {
      tracking.trackError(Object.keys(formik.errors))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.submitCount])

  return tracking
}

export default useFormTracking
